import type {
  PartnerShop,
  PartnerShopBasicState,
  PartnerShopLookbookSection,
} from '@/pages/partner/PartnerCreatePage';

export interface SearchCafeExtractedShopsParams {
  keyword?: string;
  shopCategory?: string;
  page?: number;
  rows?: number;
}

export interface CafeExtractedShopSearchItem {
  id: number;
  shopName: string;
  normalizedShopName: string;
  shopCategory: string | null;

  articleCount: number;
  directReviewCount: number;
  indirectReviewCount: number;
  imageCount: number;

  mappedPartnerShopText?: string | null;
}

export interface SearchCafeExtractedShopsResponse {
  page: number;
  rows: number;
  total: number;
  list: CafeExtractedShopSearchItem[];
}

interface ApiResponse<T> {
  status: number;
  data: T;
  message?: string;
}

export type PartnerShopStatus = 'DRAFT' | 'ACTIVE' | 'HIDDEN';
export type PartnerShopExtractedShopRelationType =
  | 'PRIMARY'
  | 'ALIAS'
  | 'BRANCH'
  | 'MERGED';

export interface GetPartnerShopsParams {
  page?: number;
  rows?: number;
  keyword?: string;
  shopCategory?: string;
  status?: PartnerShopStatus;
}

export interface PartnerShopExtractedShopListItem {
  mapId: number;
  cafeExtractedShopId: number;
  shopName: string;
  normalizedShopName: string;
  shopCategory: string | null;
  relationType: PartnerShopExtractedShopRelationType;
  memo: string | null;
  articleCount: number;
  directReviewCount: number;
  imageCount: number;
}

export interface PartnerShopListItem {
  id: number;
  shopName: string;
  displayName: string | null;
  shopSlug: string;
  normalizedShopName: string;
  shopCategory: string | null;
  branchName: string | null;
  areaName: string | null;
  shortDescription: string | null;
  heroImageUrl: string | null;
  isCertified: number;
  status: PartnerShopStatus;
  sortOrder: number;
  extractedShops: PartnerShopExtractedShopListItem[];
  extractedShopCount: number;
  reviewItemCount: number;
  publishedReviewItemCount: number;
  reviewPhotoCount: number;
  publishedReviewPhotoCount: number;
  lookbookSectionCount: number;
  lookbookPhotoCount: number;
  hasReviewSummary: boolean;
  reviewSummaryStatus: string | null;
  reviewSummaryText: string | null;
  creationTime: string;
  mdfTime: string;
}

export interface GetPartnerShopsResponse {
  page: number;
  rows: number;
  total: number;
  list: PartnerShopListItem[];
}

export interface PartnerShopPlannerContentPhotoRef {
  photoKey: string;
  caption?: string;
  alt?: string;
}

export interface PartnerShopPlannerPhoto extends PartnerShopPlannerContentPhotoRef {
  partnerShopReviewPhotoId?: number | string;
  imageUrl: string;
  thumbnailUrl?: string;
  imageUrlW720?: string;
  imageUrlW1080?: string;
  width?: number;
  height?: number;
  photoCategory?: string;
  imageCategoryName?: string;
  imageSummary?: string;
  usableForApp?: boolean;
  isRepresentative?: boolean;
}

export type PartnerShopPlannerContentPhoto =
  | PartnerShopPlannerPhoto
  | PartnerShopPlannerContentPhotoRef;

export interface PartnerShopPlannerContent {
  schemaVersion?: string;
  partnerShopId: number;
  shopName?: string;
  eyebrow?: string;
  headline: string;
  introduction?: string;
  heroPhotos?: PartnerShopPlannerContentPhoto[];
  sections?: Array<{
    title: string;
    body: string;
    factIds?: string[];
    photos?: PartnerShopPlannerPhoto[];
    photoKeys?: string[];
  }>;
  plannerQuote?: {
    text: string;
    factIds?: string[];
  };
}

export interface PartnerShopPlannerContentListItem {
  id: number;
  partnerShopId: number;
  currentPlannerContentYn: number;
  contentJson: PartnerShopPlannerContent;
  creationTime: string;
  mdfTime: string;
}

export interface GetPartnerShopPlannerContentsResponse {
  page: number;
  rows: number;
  total: number;
  list: PartnerShopPlannerContentListItem[];
}

export interface GeneratePartnerShopPlannerContentResponse {
  id: number;
  partnerShopId: number;
  currentPlannerContentYn: number;
  contentJson: PartnerShopPlannerContent;
}

export interface SelectPartnerShopPlannerContentResponse {
  id: number;
  partnerShopId: number;
  currentPlannerContentYn: number;
  contentJson: PartnerShopPlannerContent;
}

export async function getPartnerShopPlannerContents(
  partnerShopId: number,
  params: { page?: number; rows?: number } = {}
): Promise<GetPartnerShopPlannerContentsResponse> {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    searchParams.append(key, String(value));
  });

  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/planner-content?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as ApiResponse<GetPartnerShopPlannerContentsResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '플래너 콘텐츠 목록을 불러오지 못했습니다.');
  }

  return result.data;
}

export async function generatePartnerShopPlannerContent(
  partnerShopId: number
): Promise<GeneratePartnerShopPlannerContentResponse> {
  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/planner-content`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as ApiResponse<GeneratePartnerShopPlannerContentResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '플래너 콘텐츠 생성 중 오류가 발생했습니다.');
  }

  return result.data;
}

export async function selectPartnerShopPlannerContent(
  partnerShopId: number,
  plannerContentId: number
): Promise<SelectPartnerShopPlannerContentResponse> {
  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/planner-content/${plannerContentId}/select`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as ApiResponse<SelectPartnerShopPlannerContentResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '플래너 추천 콘텐츠 반영 중 오류가 발생했습니다.');
  }

  return result.data;
}

export async function getPartnerShops(
  params: GetPartnerShopsParams = {}
): Promise<GetPartnerShopsResponse> {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.append(key, String(value));
  });

  const response = await fetch(`/api/admin/partner-shops?${searchParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const result = (await response.json()) as ApiResponse<GetPartnerShopsResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '인증업체 목록 조회 중 오류가 발생했습니다.');
  }

  return result.data;
}

export interface UpdatePartnerShopSortOrderResponse {
  partnerShopId: number;
  shopCategory: string;
  sortOrder: number;
  list: Array<{
    id: number;
    displayName: string;
    sortOrder: number;
  }>;
}

export async function updatePartnerShopSortOrder(
  partnerShopId: number,
  sortOrder: number
): Promise<UpdatePartnerShopSortOrderResponse> {
  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/sort-order`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sortOrder }),
    }
  );

  const result = (await response.json()) as ApiResponse<UpdatePartnerShopSortOrderResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '인증업체 노출 순위 변경 중 오류가 발생했습니다.');
  }

  return result.data;
}

export interface SavePartnerShopExtractedShopMapPayload {
  cafeExtractedShopId: number;
  relationType: PartnerShopExtractedShopRelationType;
  memo: string | null;
  delYn: number;
}

export interface SavePartnerShopReviewItemPayload {
  cafeArticleAnalysisShopId: number;
  isPublished: number;
  sortOrder: number;
  delYn: number;
}

export interface SavePartnerShopReviewPhotoPayload {
  cafeArticleImageId: number;
  cafeArticleImageShopId: number;
  shopImageCategoryId: number | null;
  caption: string;
  isRepresentative?: number;
  isPublished: number;
  sortOrder: number;
  delYn: number;
}

export interface SavePartnerShopExtractedShopsPayload {
  partnerShopExtractedShopMaps: SavePartnerShopExtractedShopMapPayload[];
  partnerShopReviewItems?: SavePartnerShopReviewItemPayload[];
  partnerShopReviewPhotos?: SavePartnerShopReviewPhotoPayload[];
}

export interface SavePartnerShopExtractedShopsResponse {
  partnerShopId: number;
  partnerShopExtractedShopMaps: Array<{
    id: number;
    partnerShopId: number;
    cafeExtractedShopId: number;
    relationType: PartnerShopExtractedShopRelationType;
    memo: string | null;
    delYn: number;
  }>;
  savedReviewItemCount?: number;
  savedReviewPhotoCount?: number;
}

export async function savePartnerShopExtractedShops(
  partnerShopId: number,
  payload: SavePartnerShopExtractedShopsPayload
): Promise<SavePartnerShopExtractedShopsResponse> {
  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/extracted-shops`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  const result = (await response.json()) as ApiResponse<SavePartnerShopExtractedShopsResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || 'AI 추출 업체 연결 저장 중 오류가 발생했습니다.');
  }

  return result.data;
}

export interface GetPartnerShopSelectedReviewPhotoIdsResponse {
  partnerShopId: number;
  reviewPhotoIds: number[];
  cafeArticleImageShopIds: number[];
  cafeArticleImageIds: number[];
  partnerShopReviewPhotoIds: number[];
}

export async function getPartnerShopSelectedReviewPhotoIds(
  partnerShopId: number
): Promise<GetPartnerShopSelectedReviewPhotoIdsResponse> {
  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/review-photo-ids`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as ApiResponse<GetPartnerShopSelectedReviewPhotoIdsResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '선택된 후기 사진 ID 목록 조회 중 오류가 발생했습니다.');
  }

  return result.data;
}

export interface GetPartnerShopSelectedReviewIdsResponse {
  partnerShopId: number;
  reviewIds: number[];
  cafeArticleAnalysisShopIds: number[];
  partnerShopReviewItemIds: number[];
}

export async function getPartnerShopSelectedReviewIds(
  partnerShopId: number
): Promise<GetPartnerShopSelectedReviewIdsResponse> {
  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/review-ids`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as ApiResponse<GetPartnerShopSelectedReviewIdsResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '선택된 후기 ID 목록 조회 중 오류가 발생했습니다.');
  }

  return result.data;
}

export interface GetPartnerShopBasicInfoLookbookResponse {
  partnerShop: PartnerShop & { id: number };
  partnerShopLookbookSections: PartnerShopLookbookSection[];
  orphanLookbookPhotos?: unknown[];
}

export async function getPartnerShopBasicInfoLookbook(
  partnerShopId: number
): Promise<GetPartnerShopBasicInfoLookbookResponse> {
  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/basic-info-lookbook`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as ApiResponse<GetPartnerShopBasicInfoLookbookResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '기본정보와 룩북 조회 중 오류가 발생했습니다.');
  }

  return result.data;
}

export interface SavePartnerShopBasicInfoLookbookResponse {
  partnerShopId: number;
  savedLookbookSectionCount: number;
  savedLookbookPhotoCount: number;
}

export async function savePartnerShopBasicInfoLookbook(
  partnerShopId: number,
  formData: FormData
): Promise<SavePartnerShopBasicInfoLookbookResponse> {
  const response = await fetch(
    `/api/admin/partner-shops/${partnerShopId}/basic-info-lookbook`,
    {
      method: 'PUT',
      body: formData,
    }
  );

  const result = (await response.json()) as ApiResponse<SavePartnerShopBasicInfoLookbookResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '기본정보와 룩북 저장 중 오류가 발생했습니다.');
  }

  return result.data;
}

export async function searchCafeExtractedShops(
  params: SearchCafeExtractedShopsParams = {}
): Promise<SearchCafeExtractedShopsResponse> {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.append(key, String(value));
  });

  const response = await fetch(
    `/api/admin/partner-shops/cafe-extracted-shops?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as ApiResponse<SearchCafeExtractedShopsResponse>;

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || 'AI 추출 업체 목록 조회 중 오류가 발생했습니다.');
  }

  return result.data;
}



export interface GetPartnerShopBasicInfoResponse extends PartnerShopBasicState {
  id: number;
}

export async function getPartnerShopBasicInfo(
  partnerShopId: number
): Promise<GetPartnerShopBasicInfoResponse> {
  const response = await fetch(`/admin/partner-shops/${partnerShopId}/basic-info`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const result = (await response.json()) as {
    status: number;
    data: GetPartnerShopBasicInfoResponse;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '업체 기본 정보를 불러오지 못했습니다.');
  }

  return result.data;
}


export interface UploadedImageResult {
  imageUrl: string;
  thumbnailUrl?: string;
  imageUrlW720?: string;
  imageUrlW1080?: string;
}

export async function uploadLookbookImage(file: File): Promise<UploadedImageResult> {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch('/api/admin/partner-shops/lookbook-images/upload', {
    method: 'POST',
    body: formData,
  });

  const result = (await response.json()) as {
    status: number;
    data: UploadedImageResult;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '사진 업로드 중 오류가 발생했습니다.');
  }

  return result.data;
}

export interface LookbookPhotoResponse {
  id?: number;
  imageUrl: string;
  thumbnailUrl?: string;
  imageUrlW720?: string;
  imageUrlW1080?: string;
  title?: string;
  description?: string;
  isRepresentative: number;
  isPublished: number;
  sortOrder: number;
}

export interface LookbookSectionResponse {
  id?: number;
  sectionName: string;
  description: string;
  isPublished: number;
  sortOrder: number;
  photos: LookbookPhotoResponse[];
}

export interface GetLookbookResponse {
  sections: LookbookSectionResponse[];
}

export async function getPartnerShopLookbook(partnerShopId: number): Promise<GetLookbookResponse> {
  const response = await fetch(`/api/admin/partner-shops/${partnerShopId}/lookbook`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const result = (await response.json()) as {
    status: number;
    data: GetLookbookResponse;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '룩북 정보를 불러오지 못했습니다.');
  }

  return result.data;
}

export interface ReviewPhotoCandidateParams {
  cafeExtractedShopIds: number[];
  limit?: number;
}

export interface ReviewPhotoCandidateItem {
  cafeArticleImageShopId: number;
  cafeArticleImageId: number;
  sourceArticleId: number;

  categoryId: number | null;
  categoryName: string;
  categorySortOrder: number;

  imageUrl: string;
  imageUrlW300: string;
  imageUrlW720: string;
  imageUrlW1080: string;

  cafeArticleTitle: string;
  sourceUrl: string;
  confidence: number | null;
}

export interface ReviewPhotoCandidateResponse {
  total: number;
  list: ReviewPhotoCandidateItem[];
}

export async function getReviewPhotoCandidates(
  params: ReviewPhotoCandidateParams
): Promise<ReviewPhotoCandidateResponse> {
  const searchParams = new URLSearchParams();

  if (params.cafeExtractedShopIds.length > 0) {
    searchParams.append('sourceShopIds', params.cafeExtractedShopIds.join(','));
  }

  if (params.limit) {
    searchParams.append('limit', String(params.limit));
  }

  const response = await fetch(
    `/api/admin/partner-shops/review-photo-candidates?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as {
    status: number;
    data: ReviewPhotoCandidateResponse;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '후기 사진 후보를 불러오지 못했습니다.');
  }

  return result.data;
}





export interface ReviewCandidateParams {
  cafeExtractedShopIds: number[];
  limit?: number;
}

export interface ReviewPointItem {
  title: string;
  description?: string;
}

export interface ReviewCandidateItem {
  sourceReviewId: number;
  sourceArticleId: number;

  title: string;
  reviewDate: string | null;
  menuName: string | null;
  originalUrl: string | null;

  evaluationLevel: string | null;
  sentiment: string | null;
  confidence: number | null;

  summary: string;
  contentPreview: string;
  goodPoints: ReviewPointItem[];
  badPoints: ReviewPointItem[];
  tags: string[];
  thumbnailImageUrls: string[];

  evidenceText: string;
}

export interface ReviewCandidateResponse {
  total: number;
  list: ReviewCandidateItem[];
}

export async function getReviewCandidates(
  params: ReviewCandidateParams
): Promise<ReviewCandidateResponse> {
  const searchParams = new URLSearchParams();

  if (params.cafeExtractedShopIds.length > 0) {
    searchParams.append('cafeExtractedShopIds', params.cafeExtractedShopIds.join(','));
  }

  if (params.limit) {
    searchParams.append('limit', String(params.limit));
  }

  const response = await fetch(
    `/api/admin/partner-shops/review-candidates?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as {
    status: number;
    data: ReviewCandidateResponse;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '후기 후보를 불러오지 못했습니다.');
  }

  return result.data;
}




import type {
  JsonTitleDescriptionItem,
  PartnerShopReviewSummaryState,
} from '@/pages/partner/PartnerCreatePage';

export interface GenerateReviewSummaryPreviewParams {
  shopName?: string;
  sourceShopIds: number[];
}

export interface GenerateReviewSummaryPreviewResponse {
  promptVersion: string;
  modelName: string;

  reviewSummary: PartnerShopReviewSummaryState;

  verifiedReasonsJson: JsonTitleDescriptionItem[];
  checkTipsJson: JsonTitleDescriptionItem[];

  usedReviewCount: number;
  sourceReviewIdsJson: number[];
  editorNote: string;
}

export async function generateReviewSummaryPreview(
  params: GenerateReviewSummaryPreviewParams
): Promise<GenerateReviewSummaryPreviewResponse> {
  const response = await fetch('/api/admin/partner-shops/review-summary-drafts/preview', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  const result = (await response.json()) as {
    status: number;
    data: GenerateReviewSummaryPreviewResponse;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '후기 요약 초안 생성 중 오류가 발생했습니다.');
  }

  return result.data;
}







import type {
  JsonTitleDescriptionItem,
  PartnerShopReviewSummaryState,
} from '../PartnerShopCreateWizardPage';

export interface ReviewSummaryDraftItem {
  id: number;

  title: string;
  createdAt: string | null;

  modelName: string;
  promptVersion: string;

  usedReviewCount: number;

  totalMentionCount: number;
  evaluatedReviewCount: number;
  directReviewCount: number;
  indirectReviewCount: number;
  mentionedOnlyCount: number;

  summaryText: string;
  plannerText?: string | null;
  verifiedReasonsJson: JsonTitleDescriptionItem[];
  prosJson: JsonTitleDescriptionItem[];
  consJson: JsonTitleDescriptionItem[];
  keywordsJson: string[];
  checkTipsJson: JsonTitleDescriptionItem[];
  sourceCafeArticleIdsJson: number[];

  editorNote: string;
}

export interface ListReviewSummaryDraftsParams {
  cafeExtractedShopIds: number[];
  partnerShopId?: number;
}

export interface ListReviewSummaryDraftsResponse {
  list: ReviewSummaryDraftItem[];
}

export async function listReviewSummaryDrafts(
  params: ListReviewSummaryDraftsParams
): Promise<ListReviewSummaryDraftsResponse> {
  const searchParams = new URLSearchParams();

  if (params.cafeExtractedShopIds.length > 0) {
    searchParams.append('sourceShopIds', params.cafeExtractedShopIds.join(','));
  }

  if (params.partnerShopId) {
    searchParams.append('partnerShopId', String(params.partnerShopId));
  }

  const response = await fetch(
    `/api/admin/partner-shops/review-summary-drafts?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const result = (await response.json()) as {
    status: number;
    data: ListReviewSummaryDraftsResponse;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '후기 요약 초안 목록을 불러오지 못했습니다.');
  }

  return result.data;
}

export interface GenerateReviewSummaryDraftParams {
  shopName?: string;
  cafeExtractedShopIds: number[];
  partnerShopId?: number;
}

export interface GenerateReviewSummaryDraftResponse {
  draft: ReviewSummaryDraftItem;
}

export async function generateReviewSummaryDraft(
  params: GenerateReviewSummaryDraftParams
): Promise<GenerateReviewSummaryDraftResponse> {
  const response = await fetch('/api/admin/partner-shops/review-summary-drafts/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  const result = (await response.json()) as {
    status: number;
    data: GenerateReviewSummaryDraftResponse;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '후기 요약 초안 생성 중 오류가 발생했습니다.');
  }

  return result.data;
}

export async function getPartnerShopReviewSummary(
  partnerShopId: number
): Promise<PartnerShopReviewSummaryState> {
  const response = await fetch(`/api/admin/partner-shops/${partnerShopId}/review-summary`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const result = (await response.json()) as {
    status: number;
    data: PartnerShopReviewSummaryState;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '후기 요약을 불러오지 못했습니다.');
  }

  return result.data;
}

export async function savePartnerShopReviewSummary(
  partnerShopId: number,
  reviewSummary: PartnerShopReviewSummaryState
): Promise<PartnerShopReviewSummaryState> {
  const response = await fetch(`/api/admin/partner-shops/${partnerShopId}/review-summary`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(reviewSummary),
  });

  const result = (await response.json()) as {
    status: number;
    data: PartnerShopReviewSummaryState;
    message?: string;
  };

  if (!response.ok || result.status !== 200) {
    throw new Error(result.message || '후기 요약 저장 중 오류가 발생했습니다.');
  }

  return result.data;
}
